import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import attributesList from '../../../../../components/consultationNote/dropdown/attributes/attributesList';

// Define the segment type clearly
interface TranscriptionSegment {
  id: number | string;
  start: number; // seconds
  end: number;   // seconds
  text: string;
  isDelta?: boolean;
}

// Define AudioState enum
enum AudioState {
    STOPPED = 'stopped',
    RECORDING = 'recording',
    PAUSED = 'paused',
    ERROR = 'error',
    INITIALIZING = 'initializing',
}

export interface PatientInfoField {
    id: string;
    label: string; // Now stores the display label string
    value: string;
}

// Define structure for default patient info labels
export interface DefaultPatientInfoLabels {
    name: string;
    dob: string;
    healthCard: string;
}

// Define the full state interface
interface ConsultationNoteState {
    info: any;
    status: string;
    sidebarSection: string;
    consultationId: string;
    consultationNoteId: string;
    type: string;
    isSavingEnabled: boolean;
    fileUrl: string;
    mode: string;
    title: string;
    updatedTitle: string;
    audioTranscription: string;
    description: string;
    paragraphs: any[];
    selectionStart: number;
    originalTitle: string;
    titleError: string;
    action: string;
    blobFile: File | null;
    blobFileUrl: string;
    securityPin: string;
    noteFields: any[];
    segments: TranscriptionSegment[];
    additionalTranscription: string;
    additionalSegments: TranscriptionSegment[];
    isRecording: boolean;
    isAdditionalRecording: boolean;
    isTextModified: boolean;
    isSecurityPopupOpened: boolean;
    attributesQueryList: any[];
    selectedAttribute: string;
    searchQuery: string;
    manualAttributeSearchQuery: string;
    fieldSearchQuery: string;
    isAttributesDropdownOpened: boolean;
    hightlightedAttribute: string;
    isRefreshFieldsList: boolean;
    newNoteFieldTitle: string;
    newNoteFieldSelectionStart: number;
    newAttributeSelectionStart: number;
    isExistingNoteFieldTitle: boolean;
    isSidebarOpened: boolean;
    isCurrentlySaving: boolean;
    isLoadingTranscription: boolean;
    isLoadingAdditionalTranscription: boolean;
    isRefreshConsultationNote: boolean;
    isGenerateNewDescription: boolean;
    isMicrophoneDisabled: boolean;
    isRecorderBarOpened: boolean;
    isRecorderBarDisabled: boolean;
    isResetAudio: boolean;
    isResetAdditionalAudio: boolean;
    shouldAutoGenerateNote: boolean;
    editableNoteRawText: string;
    patientInfoFields: PatientInfoField[];
};

// Default English labels (ONLY for the absolute initial state before any note is loaded)
const initialEnglishLabels: DefaultPatientInfoLabels = {
    name: 'Name:',
    dob: 'DOB:',
    healthCard: 'Health Card:',
};

// This function now correctly uses the labels passed to it.
const getDefaultPatientInfoFields = (
    labels: DefaultPatientInfoLabels,
    noteIdSuffix: string = 'initial'
): PatientInfoField[] => [
    { id: `patientName-${noteIdSuffix}`, label: labels.name, value: '' },
    { id: `patientDOB-${noteIdSuffix}`, label: labels.dob, value: '' },
    { id: `patientHealthCard-${noteIdSuffix}`, label: labels.healthCard, value: '' },
];

// Define the initial state for all properties
const initialState: ConsultationNoteState = {
    info: null,
    status: '',
    sidebarSection: 'code',
    consultationId: '',
    consultationNoteId: '',
    type: '',
    isSavingEnabled: false,
    fileUrl: '',
    mode: '',
    title: '',
    updatedTitle: '',
    audioTranscription: '',
    description: '',
    paragraphs: [],
    selectionStart: 0,
    originalTitle: '',
    titleError: '',
    action: '',
    blobFile: null,
    blobFileUrl: '',
    securityPin: '',
    noteFields: [],
    segments: [],
    additionalTranscription: '',
    additionalSegments: [],
    isRecording: false,
    isAdditionalRecording: false,
    isTextModified: false,
    isSecurityPopupOpened: false,
    attributesQueryList: attributesList,
    selectedAttribute: '',
    searchQuery: '',
    manualAttributeSearchQuery: '',
    fieldSearchQuery: '',
    isAttributesDropdownOpened: false,
    hightlightedAttribute: attributesList.length > 0 ? attributesList[0] : '',
    isRefreshFieldsList: false,
    newNoteFieldTitle: '',
    newNoteFieldSelectionStart: 0,
    newAttributeSelectionStart: 0,
    isExistingNoteFieldTitle: false,
    isSidebarOpened: true,
    isCurrentlySaving: false,
    isLoadingTranscription: false,
    isLoadingAdditionalTranscription: false,
    isRefreshConsultationNote: false,
    isGenerateNewDescription: false,
    isMicrophoneDisabled: false,
    isRecorderBarOpened: false,
    isRecorderBarDisabled: false,
    isResetAudio: false,
    isResetAdditionalAudio: false,
    shouldAutoGenerateNote: false,
    editableNoteRawText: '',
    patientInfoFields: getDefaultPatientInfoFields(initialEnglishLabels),
};

// Create the slice
const consultationNoteSlice = createSlice({
  name: 'consultationNote',
  initialState,
  reducers: {
    setInfo: (state, action: PayloadAction<any>) => {
      state.info = action.payload;
    },
    setStatus: (state, action: PayloadAction<string>) => {
      state.status = action.payload;
    },
    setSidebarSection: (state, action: PayloadAction<string>) => {
      state.sidebarSection = action.payload;
    },
    setConsultationId: (state, action: PayloadAction<string>) => {
      state.consultationId = action.payload;
    },
    // REVAMPED: This action now REQUIRES translated labels to be passed in.
    setConsultationNoteId: (state, action: PayloadAction<{ id: string; labels: DefaultPatientInfoLabels }>) => {
      state.consultationNoteId = action.payload.id;
      // No more fallback to English. The provided labels are always used.
      state.patientInfoFields = getDefaultPatientInfoFields(action.payload.labels, action.payload.id || 'new');
    },
    setType: (state, action: PayloadAction<string>) => {
      state.type = action.payload;
      if ((action.payload === 'template') && (state.sidebarSection === 'code')) {
        state.sidebarSection = 'fields';
      };
    },
    setIsSavingEnabled: (state, action: PayloadAction<boolean>) => {
      state.isSavingEnabled = action.payload;
    },
    setFileUrl: (state, action: PayloadAction<string>) => {
      state.fileUrl = action.payload;
    },
    setMode: (state, action: PayloadAction<string>) => {
      state.mode = action.payload;
    },
    setTitle: (state, action: PayloadAction<string>) => {
      state.title = action.payload;
    },
    setAudioTranscription: (state, action: PayloadAction<string>) => {
      state.audioTranscription = action.payload;
    },
    setUpdatedTitle: (state, action: PayloadAction<string>) => {
      state.updatedTitle = action.payload;
    },
    setDescription: (state, action: PayloadAction<string>) => {
      state.description = action.payload;
    },
    setParagraphs: (state, action: PayloadAction<any[]>) => {
      state.paragraphs = action.payload;
    },
    setSelectionStart: (state, action: PayloadAction<number>) => {
      state.selectionStart = action.payload;
    },
    setOriginalTitle: (state, action: PayloadAction<string>) => {
      state.originalTitle = action.payload;
    },
    setTitleError: (state, action: PayloadAction<string>) => {
      state.titleError = action.payload;
      state.isSavingEnabled = action.payload ? false : state.isSavingEnabled;
    },
    setAction: (state, action: PayloadAction<string>) => {
      state.action = action.payload;
    },
    setBlobFile: (state, action: PayloadAction<File | null>) => {
      state.blobFile = action.payload;
    },
    setBlobFileUrl: (state, action: PayloadAction<string>) => {
      state.blobFileUrl = action.payload;
    },
    setSecurityPin: (state, action: PayloadAction<string>) => {
      state.securityPin = action.payload;
    },
    setNoteFields: (state, action: PayloadAction<any[]>) => {
      state.noteFields = action.payload;
    },
    setIsTextModified: (state, action: PayloadAction<boolean>) => {
      state.isTextModified = action.payload;
    },
    setIsSecurityPopupOpened: (state, action: PayloadAction<boolean>) => {
      state.isSecurityPopupOpened = action.payload;
    },
    setAttributesQueryList: (state, action: PayloadAction<any[]>) => {
      state.attributesQueryList = action.payload;
    },
    setSelectedAttribute: (state, action: PayloadAction<string>) => {
      state.selectedAttribute = action.payload;
    },
    setSearchQuery: (state, action: PayloadAction<string>) => {
      state.searchQuery = action.payload;
    },
    setManualAttributeSearchQuery: (state, action: PayloadAction<string>) => {
      state.manualAttributeSearchQuery = action.payload;
    },
    setFieldSearchQuery: (state, action: PayloadAction<string>) => {
      state.fieldSearchQuery = action.payload;
    },
    setIsAttributesDropdownOpened: (state, action: PayloadAction<boolean>) => {
      state.isAttributesDropdownOpened = action.payload;
    },
    setHightlightedAttribute: (state, action: PayloadAction<string>) => {
      state.hightlightedAttribute = action.payload;
    },
    setIsRefreshFieldsList: (state, action: PayloadAction<boolean>) => {
      state.isRefreshFieldsList = action.payload;
    },
    setNewNoteFieldTitle: (state, action: PayloadAction<string>) => {
      state.newNoteFieldTitle = action.payload;
    },
    setNewNoteFieldSelectionStart: (state, action: PayloadAction<number>) => {
      state.newNoteFieldSelectionStart = action.payload;
    },
    setNewAttributeSelectionStart: (state, action: PayloadAction<number>) => {
      state.newAttributeSelectionStart = action.payload;
    },
    setIsExistingNoteFieldTitle: (state, action: PayloadAction<boolean>) => {
      state.isExistingNoteFieldTitle = action.payload;
    },
    setIsSidebarOpened: (state, action: PayloadAction<boolean>) => {
      state.isSidebarOpened = action.payload;
    },
    setIsCurrentlySaving: (state, action: PayloadAction<boolean>) => {
      state.isCurrentlySaving = action.payload;
    },
    setIsRefreshConsultationNote: (state, action: PayloadAction<boolean>) => {
      state.isRefreshConsultationNote = action.payload;
    },
    setSegments: (state, action: PayloadAction<TranscriptionSegment[]>) => {
      state.segments = action.payload;
    },
    appendSegment: (state, action: PayloadAction<TranscriptionSegment>) => {
      const newSegment = action.payload;
      const existingIndex = state.segments.findIndex(s => s.id === newSegment.id);
      if (existingIndex > -1) {
        state.segments[existingIndex] = newSegment;
      } else {
        state.segments.push(newSegment);
      }
    },
    setIsRecording: (state, action: PayloadAction<boolean>) => {
      state.isRecording = action.payload;
    },
    setIsLoadingTranscription: (state, action: PayloadAction<boolean>) => {
      state.isLoadingTranscription = action.payload;
    },
    setIsGenerateNewDescription: (state, action: PayloadAction<boolean>) => {
      state.isGenerateNewDescription = action.payload;
    },
    setIsMicrophoneDisabled: (state, action: PayloadAction<boolean>) => {
      state.isMicrophoneDisabled = action.payload;
    },
    setIsRecorderBarOpened: (state, action: PayloadAction<boolean>) => {
      state.isRecorderBarOpened = action.payload;
    },
    setIsRecorderBarDisabled: (state, action: PayloadAction<boolean>) => {
      state.isRecorderBarDisabled = action.payload;
    },
    setIsResetAudio: (state, action: PayloadAction<boolean>) => {
      state.isResetAudio = action.payload;
    },
    setAdditionalTranscription: (state, action: PayloadAction<string>) => {
      state.additionalTranscription = action.payload;
    },
    setAdditionalSegments: (state, action: PayloadAction<TranscriptionSegment[]>) => {
      state.additionalSegments = action.payload;
    },
    appendAdditionalSegment: (state, action: PayloadAction<TranscriptionSegment>) => {
      const newSegment = action.payload;
      const existingIndex = state.additionalSegments.findIndex(s => s.id === newSegment.id);
      if (existingIndex > -1) {
        state.additionalSegments[existingIndex] = newSegment;
      } else {
        state.additionalSegments.push(newSegment);
      }
    },
    setIsAdditionalRecording: (state, action: PayloadAction<boolean>) => {
      state.isAdditionalRecording = action.payload;
    },
    setIsLoadingAdditionalTranscription: (state, action: PayloadAction<boolean>) => {
      state.isLoadingAdditionalTranscription = action.payload;
    },
    setIsResetAdditionalAudio: (state, action: PayloadAction<boolean>) => {
        state.isResetAdditionalAudio = action.payload;
    },
    setShouldAutoGenerateNote: (state, action: PayloadAction<boolean>) => {
        state.shouldAutoGenerateNote = action.payload;
    },
    setEditableNoteRawText: (state, action: PayloadAction<string>) => {
        state.editableNoteRawText = action.payload;
    },
    setPatientInfoFields: (state, action: PayloadAction<PatientInfoField[]>) => {
        state.patientInfoFields = action.payload;
    },
    updatePatientInfoField: (state, action: PayloadAction<{ id: string; label?: string; value?: string }>) => {
        const { id, label, value } = action.payload;
        const field = state.patientInfoFields.find(f => f.id === id);
        if (field) {
            if (label !== undefined) field.label = label;
            if (value !== undefined) field.value = value;
        }
    },
    addPatientInfoField: (state, action: PayloadAction<{ label: string }>) => {
        state.patientInfoFields.push({
            id: `custom-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
            label: action.payload.label,
            value: ''
        });
    },
    removePatientInfoField: (state, action: PayloadAction<{ id: string }>) => {
        state.patientInfoFields = state.patientInfoFields.filter(f => f.id !== action.payload.id);
    },
    // REVAMPED: This action also requires translated labels.
    resetConsultationNote: (state, action: PayloadAction<{ labels: DefaultPatientInfoLabels }>) => {
        const labelsToUse = action.payload.labels;
        Object.assign(state, {
            ...initialState,
            attributesQueryList: attributesList,
            hightlightedAttribute: attributesList.length > 0 ? attributesList[0] : '',
            patientInfoFields: getDefaultPatientInfoFields(labelsToUse, 'reset'),
        });
    },
    // REMOVED: The reactive patching reducer is deleted.
    // applyTranslatedDefaultLabels: (...)
  },
});

export const {
  setInfo,
  setStatus,
  setSidebarSection,
  setConsultationId,
  setConsultationNoteId,
  setType,
  setIsSavingEnabled,
  setFileUrl,
  setMode,
  setTitle,
  setAudioTranscription,
  setUpdatedTitle,
  setDescription,
  setParagraphs,
  setSelectionStart,
  setOriginalTitle,
  setTitleError,
  setAction,
  setBlobFile,
  setBlobFileUrl,
  setSecurityPin,
  setNoteFields,
  setSegments,
  appendSegment,
  setIsRecording,
  setIsTextModified,
  setAttributesQueryList,
  setIsSecurityPopupOpened,
  setSearchQuery,
  setManualAttributeSearchQuery,
  setFieldSearchQuery,
  setSelectedAttribute,
  setIsAttributesDropdownOpened,
  setHightlightedAttribute,
  setIsRefreshFieldsList,
  setNewNoteFieldSelectionStart,
  setNewAttributeSelectionStart,
  setNewNoteFieldTitle,
  setIsExistingNoteFieldTitle,
  setIsSidebarOpened,
  setIsCurrentlySaving,
  setIsLoadingTranscription,
  setIsRefreshConsultationNote,
  setIsGenerateNewDescription,
  setIsMicrophoneDisabled,
  setIsRecorderBarOpened,
  setIsRecorderBarDisabled,
  setIsResetAudio,
  setAdditionalTranscription,
  setAdditionalSegments,
  appendAdditionalSegment,
  setIsAdditionalRecording,
  setIsLoadingAdditionalTranscription,
  setIsResetAdditionalAudio,
  setShouldAutoGenerateNote,
  setEditableNoteRawText,
  setPatientInfoFields,
  updatePatientInfoField,
  addPatientInfoField,
  removePatientInfoField,
  resetConsultationNote,
} = consultationNoteSlice.actions;

export default consultationNoteSlice.reducer;